import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import ScrollToTop from "@/components/layout/ScrollToTop";
import motion from "@/lib/lazy-motion-tags";
import { Helmet } from "react-helmet";
import IconLoader from "@/components/IconLoader";
import { Card } from "@/components/ui/card";

const Services = () => {
  const services = [
    {
      iconName: "Ruler",
      title: "استشارة وتصميم مجاني",
      description: "نقدم لك استشارة مجانية لمساعدتك في اختيار التصميم والمقاس المناسب لمشروعك.",
    },
    {
      iconName: "Shield",
      title: "ضمان شامل",
      description: "جميع منتجاتنا مغطاة بضمان شامل يحميك من أي عيوب في التصنيع أو المواد.",
    },
    {
      iconName: "Truck",
      title: "توصيل وتركيب احترافي",
      description: "فريق فني متخصص يضمن تركيب سليم وسريع للأبواب في موقع مشروعك.",
    },
    {
      iconName: "Users",
      title: "دعم فني متواصل",
      description: "فريق دعم جاهز للإجابة على استفساراتك ومساعدتك في أي وقت.",
    },
    {
      iconName: "Clock",
      title: "سرعة في التنفيذ",
      description: "نلتزم بمواعيد التسليم ونعمل على إنجاز مشروعك في الوقت المحدد.",
    },
    {
      iconName: "Award",
      title: "جودة مضمونة",
      description: "جميع منتجاتنا تخضع لاختبارات صارمة لضمان أعلى معايير الجودة.",
    },
  ];

  const process = [
    {
      step: "01",
      title: "التواصل والاستشارة",
      description: "تواصل معنا عبر الهاتف أو الموقع، وسنقدم لك استشارة مجانية لفهم احتياجاتك.",
    },
    {
      step: "02",
      title: "المعاينة والقياس",
      description: "نزور موقع المشروع لأخذ القياسات الدقيقة ومناقشة التفاصيل معك.",
    },
    {
      step: "03",
      title: "عرض السعر",
      description: "نقدم لك عرض سعر شامل وواضح يتضمن جميع التفاصيل والتكاليف.",
    },
    {
      step: "04",
      title: "التصنيع",
      description: "نبدأ في تصنيع الأبواب وفقاً للمواصفات المتفق عليها بأعلى معايير الجودة.",
    },
    {
      step: "05",
      title: "التوصيل والتركيب",
      description: "فريقنا يوصل الأبواب ويركبها بشكل احترافي في موقع المشروع.",
    },
    {
      step: "06",
      title: "المتابعة والدعم",
      description: "نتابع معك بعد التركيب لضمان رضاك التام ونقدم الدعم المستمر.",
    },
  ];

  const commitments = [
    "التزام بأعلى معايير الجودة العالمية",
    "مواعيد تسليم محددة ونلتزم بها",
    "أسعار شفافة بدون تكاليف مخفية",
    "خدمة ما بعد البيع متميزة",
    "ضمان شامل على جميع المنتجات",
    "فريق فني مدرب ومحترف",
  ];

  return (
    <>
      <ScrollToTop />
      <Helmet>
        <title>خدماتنا والتزاماتنا - New Business | تركيب احترافي وضمان شامل</title>
        <meta name="description" content="نقدم خدمات متكاملة من الاستشارة والتصميم إلى التركيب والدعم الفني. ضمان شامل وجودة مضمونة" />
      </Helmet>
      
      <div className="min-h-screen">
        <Navbar />
        
        {/* Hero */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-background via-muted/30 to-background">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="text-gradient">خدماتنا</span> والتزاماتنا
              </h1>
              <p className="text-xl text-muted-foreground">
                نقدم لك تجربة متكاملة من البداية للنهاية
              </p>
            </motion.div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold mb-6">الخدمات التي نقدمها</h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card className="p-8 hover-lift h-full">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-primary flex items-center justify-center mb-6 shadow-elegant">
                        <IconLoader name={service.iconName} className="h-8 w-8 text-accent" />
                      </div>
                      <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{service.description}</p>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-24 bg-card">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold mb-6">كيف نعمل معك؟</h2>
              <p className="text-xl text-muted-foreground">رحلة سلسة من الاستشارة إلى التركيب النهائي</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {process.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="p-8 hover-lift h-full relative overflow-hidden">
                    <div className="absolute top-0 right-0 text-8xl font-bold text-primary/5">{item.step}</div>
                    <div className="relative">
                      <div className="text-4xl font-bold text-gradient mb-4">{item.step}</div>
                      <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Commitments */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold mb-6">
                <span className="text-gradient">التزاماتنا</span> تجاهك
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {commitments.map((commitment, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-4 p-6 rounded-xl bg-card hover:bg-muted/50 transition-colors"
                >
                  <IconLoader name="CheckCircle2" className="h-8 w-8 text-primary flex-shrink-0" />
                  <p className="text-lg font-medium">{commitment}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Services;
